import { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Pencil, Camera, CloudUpload, ImageMinus, User, Loader2 } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import { CreateVisitFormV2Values } from '../../../../types/form-data.types';
import { FieldConfig } from '../../../../hooks/use-ui-metadata-field-config.hook';
import { useVisitorPhotoUpload } from 'hooks/use-visitor-photo-upload.hook';
import { usePhotoByUuid } from 'hooks/use-photo-by-uuid.hook';
import type { FieldError } from 'react-hook-form';

interface VisitorPhotoFieldProps {
  control: Control<CreateVisitFormV2Values>;
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
  visitorIndex: number;
  config: FieldConfig;
  error?: FieldError;
  firstName?: string;
  lastName?: string;
}

const getInitials = (firstName?: string, lastName?: string): string => {
  const firstInitial = firstName?.charAt(0)?.toUpperCase() || '';
  const lastInitial = lastName?.charAt(0)?.toUpperCase() || '';
  return firstInitial + lastInitial;
};

export const VisitorPhotoField = ({
  control,
  setValue,
  visitorIndex,
  config,
  error,
  firstName,
  lastName,
}: VisitorPhotoFieldProps): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const { uploadPhoto, isUploading } = useVisitorPhotoUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const initials = useMemo(() => getInitials(firstName, lastName), [firstName, lastName]);
  const hasError = Boolean(error);

  const photoUuidWatched = useWatch({
    control,
    name: `visitors.${visitorIndex}.photoUuid`,
  });
  const photoUuidStr =
    photoUuidWatched != null && String(photoUuidWatched).trim() !== '' ? String(photoUuidWatched).trim() : '';
  const { photoUrl: photoUrlFromUuid } = usePhotoByUuid(photoUuidStr && !previewUrl ? photoUuidStr : null);

  const uploadAndSetPhoto = useCallback(
    async (blob: Blob, previewDataUrl: string) => {
      setPreviewUrl(previewDataUrl);

      const photoUuid = await uploadPhoto(blob);
      if (photoUuid) {
        setValue(`visitors.${visitorIndex}.photoUuid` as const, photoUuid, { shouldValidate: true });
      }
    },
    [uploadPhoto, setValue, visitorIndex],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const dataUrl = reader.result as string;
          await uploadAndSetPhoto(file, dataUrl);
        };
        reader.readAsDataURL(file);
      }
      setMenuOpen(false);
    },
    [uploadAndSetPhoto],
  );

  const handleRemovePhoto = useCallback(() => {
    setPreviewUrl(null);
    setValue(`visitors.${visitorIndex}.photoUuid` as const, '', { shouldValidate: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setMenuOpen(false);
  }, [setValue, visitorIndex]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const stopCameraStream = useCallback((mediaStream: MediaStream | null) => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
    }
  }, []);

  const handleTakePhoto = useCallback(async () => {
    setMenuOpen(false);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      setStream(mediaStream);
      setShowCamera(true);
    } catch {
      // Camera access denied or not available
    }
  }, []);

  const handleCapturePhoto = useCallback(async () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

        canvas.toBlob(
          async (blob) => {
            if (blob) {
              await uploadAndSetPhoto(blob, dataUrl);
            }
          },
          'image/jpeg',
          0.8,
        );
      }
    }
    stopCameraStream(stream);
    setStream(null);
    setShowCamera(false);
  }, [uploadAndSetPhoto, stream, stopCameraStream]);

  const handleCancelCamera = useCallback(() => {
    stopCameraStream(stream);
    setStream(null);
    setShowCamera(false);
  }, [stream, stopCameraStream]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    if (videoRef.current && stream && showCamera) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, showCamera]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [menuOpen, closeMenu]);

  if (config.hidden) {
    return null;
  }

  const displaySrc = previewUrl || photoUrlFromUuid || '';

  return (
    <>
      <div className="flex flex-col items-center" ref={menuRef}>
        <div className="relative inline-block">
          <div className={cn('w-[72px] h-[72px] rounded-full overflow-hidden', hasError && 'ring-2 ring-destructive')}>
            {displaySrc ? (
              <Avatar key={displaySrc} className="w-full h-full">
                <AvatarImage
                  src={displaySrc}
                  alt={formatMessage({ id: 'createVisitFormV2.visitStep.visitorPhotoAlt' })}
                />
                <AvatarFallback className="text-lg font-medium bg-gray-100 border border-gray-200 text-gray-900">
                  {initials || <User className="w-8 h-8 text-gray-400" />}
                </AvatarFallback>
              </Avatar>
            ) : (
              <Avatar key="no-photo" className="w-full h-full">
                <AvatarFallback className="text-lg font-medium bg-gray-100 border border-gray-200 text-gray-900">
                  {initials || <User className="w-8 h-8 text-gray-400" />}
                </AvatarFallback>
              </Avatar>
            )}
          </div>

          <button
            type="button"
            onClick={toggleMenu}
            disabled={isUploading}
            className="absolute bottom-0 right-0 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
            aria-label={formatMessage({
              id: 'createVisitFormV2.visitStep.editPhoto',
              defaultMessage: 'Edit photo',
            })}
          >
            {isUploading ? (
              <Loader2 className="w-3 h-3 text-gray-600 animate-spin" />
            ) : (
              <Pencil className="w-3 h-3 text-gray-600" />
            )}
          </button>

          {menuOpen && (
            <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]">
              <button
                type="button"
                onClick={handleTakePhoto}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <Camera className="w-5 h-5" />
                {formatMessage({
                  id: 'createVisitFormV2.visitStep.takePhoto',
                  defaultMessage: 'Take photo',
                })}
              </button>
              <button
                type="button"
                onClick={handleUploadClick}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <CloudUpload className="w-5 h-5" />
                {formatMessage({
                  id: 'createVisitFormV2.visitStep.uploadPhoto',
                  defaultMessage: 'Upload photo',
                })}
              </button>
              {displaySrc && (
                <>
                  <div className="border-t border-gray-200 my-1" />
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-3"
                  >
                    <ImageMinus className="w-5 h-5" />
                    {formatMessage({
                      id: 'createVisitFormV2.visitStep.deletePhoto',
                      defaultMessage: 'Delete photo',
                    })}
                  </button>
                </>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <span className="text-xs text-gray-500 mt-1 italic">
          PNG, JPEG{config.required && <span className="text-destructive not-italic"> *</span>}
        </span>

        {hasError && (
          <p className="text-xs text-destructive mt-1">
            {formatMessage({ id: 'errors.visitorPhotoRequired', defaultMessage: 'Visitor photo is required' })}
          </p>
        )}
      </div>

      {showCamera && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-4 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {formatMessage({
                id: 'createVisitFormV2.visitStep.takePhotoTitle',
                defaultMessage: 'Take a photo',
              })}
            </h3>
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleCancelCamera}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {formatMessage({
                  id: 'createVisitFormV2.visitStep.cancel',
                  defaultMessage: 'Cancel',
                })}
              </button>
              <button
                type="button"
                onClick={handleCapturePhoto}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {formatMessage({
                  id: 'createVisitFormV2.visitStep.capture',
                  defaultMessage: 'Capture',
                })}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
